(() => {
  const root = window.Shopify?.routes?.root || '/';
  const section = document.querySelector('[data-product-section]');
  if (!section) return;

  const handle = section.dataset.handle;
  const form = section.querySelector('[data-bcos-add-to-cart]');
  const idInput = section.querySelector('[data-variant-id]');
  const priceBox = section.querySelector('[data-bcos-price]');
  const addButton = section.querySelector('[data-add-to-cart]');
  const options = [...section.querySelectorAll('[data-variant-option]')];
  const pickups = section.querySelector('bcos-pickup-availability');

  function formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  function getSelectedOptions() {
    const values = {};
    options.forEach((input) => {
      if (input.checked) values[input.closest('[data-option]').dataset.option] = input.value;
    });
    return values;
  }

  async function updateVariant(variantId) {
    if (!variantId) return;
    const res = await fetch(`${root}products/${handle}.js?variant=${variantId}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) return;
    const data = await res.json();

    if (idInput) idInput.value = data.id;

    updatePrice(data);
    updateMedia(data);
    updateButton(data);
    updateInventory(data);

    document.dispatchEvent(new CustomEvent('bcos:variant:changed', {
      detail: {
        id: data.id,
        price: data.price,
        compareAtPrice: data.compare_at_price,
      },
    }));

    if (pickups) {
      pickups.setAttribute('data-variant-id', data.id);
      if (typeof pickups.updateAvailability === 'function') pickups.updateAvailability();
    }
  }

  function markUnavailableOptions(data) {
    const variants = data?.variants || [];
    const groups = [...section.querySelectorAll('[data-option]')];
    groups.forEach((group) => {
      const position = parseInt(group.dataset.option, 10);
      const selected = getSelectedOptions();
      const labels = [...group.querySelectorAll('.bcos-variant__label')];
      const inputs = [...group.querySelectorAll('[data-variant-option]')];
      inputs.forEach((input) => {
        const value = input.value;
        const available = variants.some((variant) => {
          if (!variant.available) return false;
          const variantOptions = { 1: variant.option1, 2: variant.option2, 3: variant.option3 };
          for (const pos of Object.keys(selected)) {
            if (parseInt(pos, 10) === position) continue;
            if (selected[pos] !== variantOptions[parseInt(pos, 10)]) return false;
          }
          return variantOptions[position] === value;
        });
        const label = labels[inputs.indexOf(input)];
        if (label) label.classList.toggle('bcos-variant__label--unavailable', !available);
      });
    });
  }

  function loadAvailability() {
    fetch(`${root}products/${handle}.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((res) => res.json())
      .then((data) => markUnavailableOptions(data))
      .catch(() => {});
  }

  function updatePrice(data) {
    if (!priceBox) return;
    const current = priceBox.querySelector('[data-bcos-price-current]');
    const compare = priceBox.querySelector('[data-bcos-price-compare]');
    const save = priceBox.querySelector('[data-bcos-price-save]');
    const unit = priceBox.querySelector('[data-bcos-price-unit]');
    const onSale = data.compare_at_price > data.price;

    if (current) {
      current.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.textContent = formatMoney(data.price);
      });
      const from = current.querySelector('.bcos-price__from');
      if (from) from.remove();
    }

    if (compare) {
      compare.textContent = formatMoney(data.compare_at_price);
      compare.style.display = onSale ? '' : 'none';
    }
    if (save) {
      save.textContent = `Save ${formatMoney(data.compare_at_price - data.price)}`;
      save.style.display = onSale ? '' : 'none';
    }
    priceBox.classList.toggle('bcos-price--sale', onSale);

    if (unit) {
      unit.textContent = data.unit_price
        ? `${formatMoney(data.unit_price)} / ${data.unit_price_measurement?.reference_value || ''}${data.unit_price_measurement?.reference_unit || ''}`
        : '';
      unit.style.display = data.unit_price ? '' : 'none';
    }
  }

  function updateMedia(data) {
    const mediaId = data.featured_media?.id;
    if (!mediaId) return;
    const slides = [...section.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...section.querySelectorAll('[data-gallery-thumb]')];
    const active = slides.find((slide) => slide.dataset.mediaId === String(mediaId));
    if (!active) return;

    slides.forEach((slide) => slide.classList.remove('bcos-gallery__slide--active'));
    active.classList.add('bcos-gallery__slide--active');
    thumbs.forEach((thumb) => {
      thumb.classList.toggle('bcos-gallery__thumb--active', thumb.dataset.mediaId === String(mediaId));
    });
  }

  function updateButton(data) {
    if (!addButton) return;
    const available = data.available;
    addButton.disabled = !available;
    addButton.textContent = available ? 'Add to cart' : 'Sold out';
  }

  function updateInventory(data) {
    const status = section.querySelector('[data-inventory-status]');
    if (!status) return;
    const message = status.querySelector('[data-inventory-message]');
    const item = status.querySelector('.bcos-inventory-status__item');
    if (!message || !item) return;

    item.classList.remove(
      'bcos-inventory-status__item--in-stock',
      'bcos-inventory-status__item--low',
      'bcos-inventory-status__item--sold-out',
      'bcos-inventory-status__item--preorder'
    );

    const threshold = parseInt(status.dataset.threshold || '5', 10);
    const isLow = data.available && data.inventory_management && data.inventory_policy === 'deny' && data.inventory_quantity > 0 && data.inventory_quantity <= threshold;

    if (!data.available) {
      item.classList.add('bcos-inventory-status__item--sold-out');
      message.textContent = 'Sold out';
    } else if (isLow) {
      item.classList.add('bcos-inventory-status__item--low');
      message.textContent = `Only ${data.inventory_quantity} left`;
    } else {
      item.classList.add('bcos-inventory-status__item--in-stock');
      message.textContent = 'In stock';
    }
    status.hidden = false;

    const qtyContainer = section.querySelector('[data-quantity]');
    if (qtyContainer) {
      const maxAllowed = data.available && data.inventory_management && data.inventory_policy === 'deny'
        ? data.inventory_quantity
        : parseInt(qtyContainer.dataset.quantityMax, 10);
      if (Number.isFinite(maxAllowed) && maxAllowed > 0) {
        qtyContainer.dataset.quantityMax = maxAllowed;
        const qtyInput = qtyContainer.querySelector('[data-quantity-input]');
        if (qtyInput) {
          qtyInput.max = maxAllowed;
          if (parseInt(qtyInput.value, 10) > maxAllowed) {
            qtyInput.value = Math.max(parseInt(qtyContainer.dataset.quantityMin, 10) || 1, maxAllowed);
          }
        }
      }
    }
  }

  options.forEach((input) => {
    input.addEventListener('change', () => {
      const values = getSelectedOptions();
      const query = new URLSearchParams();
      Object.keys(values).forEach((key) => query.append('options[' + key + ']', values[key]));
      fetch(`${root}products/${handle}.js?${query.toString()}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((res) => res.json())
        .then((data) => {
          markUnavailableOptions(data);
          updateVariant(data.id);
        })
        .catch(() => {});
    });
  });

  loadAvailability();

  const thumbs = [...section.querySelectorAll('[data-gallery-thumb]')];
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      thumbs.forEach((t) => t.classList.toggle('bcos-gallery__thumb--active', t === thumb));
      section.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
        slide.classList.toggle('bcos-gallery__slide--active', slide.dataset.mediaId === thumb.dataset.mediaId);
      });
    });
  });

  document.addEventListener('submit', (event) => {
    const quantityForm = event.target.closest('[data-bcos-add-to-cart]');
    if (!quantityForm || quantityForm !== form) return;
    const qtyInput = section.querySelector('[data-quantity-input]');
    const hidden = form.querySelector('[data-quantity-hidden]');
    if (hidden && qtyInput) hidden.value = qtyInput.value;
  });
  const recommendations = section.querySelector('[data-related-products]');
  if (recommendations && recommendations.dataset.url) {
    fetch(recommendations.dataset.url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((res) => res.text())
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html;
        const rendered = template.content.querySelector('[data-related-products]');
        if (rendered && rendered.children.length) {
          recommendations.replaceWith(rendered);
        }
      })
      .catch(() => {});
  }

  const recipient = section.querySelector('[data-gift-recipient]');
  if (recipient) {
    const toggle = recipient.querySelector('[data-gift-recipient-toggle]');
    const inputs = recipient.querySelector('[data-gift-recipient-inputs]');
    const required = [...recipient.querySelectorAll('[data-gift-recipient-required]')];
    if (toggle && inputs) {
      toggle.addEventListener('change', () => {
        inputs.hidden = !toggle.checked;
        required.forEach((input) => (input.disabled = !toggle.checked));
      });
    }
  }
})();
